import { useFetch } from "~vue/api";
import type { EmailTeamInvitationManagement, ManualTeamInvitationManagement } from "~vue/api/generated/team-invitations";
import { type MaybeRefOrGetter, onScopeDispose, ref, shallowRef, toValue, watch } from "vue";

export function useTeamInvitationManagement(teamId: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean> = true) {
    const management = shallowRef<EmailTeamInvitationManagement | ManualTeamInvitationManagement>();
    const loading = ref(false);
    const failed = ref(false);
    let generation = 0;
    onScopeDispose(() => generation++);
    async function load() {
        const current = ++generation;
        failed.value = false;
        loading.value = toValue(enabled);
        if (!loading.value) {
            return;
        }
        try {
            const { data, error } = await useFetch(`/v1/teams/${toValue(teamId)}/invitations`)
                .get()
                .json<EmailTeamInvitationManagement | ManualTeamInvitationManagement>();
            if (current !== generation) {
                return;
            }
            if (error.value || !data.value) {
                management.value = undefined;
                failed.value = true;
            } else {
                management.value = data.value;
            }
        } catch {
            if (current === generation) {
                management.value = undefined;
                failed.value = true;
            }
        } finally {
            if (current === generation) {
                loading.value = false;
            }
        }
    }
    watch(
        [() => toValue(teamId), () => toValue(enabled)],
        () => {
            management.value = undefined;
            void load();
        },
        { immediate: true },
    );
    return { management, loading, failed, load };
}

export type TeamInvitationManagementState = ReturnType<typeof useTeamInvitationManagement>;
